import { useState, useEffect, useCallback, useRef } from 'react';
import { getLocal, setLocal } from '@/utils/storage';
import { useFeedStatus } from '@/hooks/useFeedStatus';
import { useSocketFeed } from '@/hooks/useSocketFeed';
import {
  getOptionChainInstrumentNames,
  getSpotToken,
  getOptionExpiries,
  getOptionChainInstrumentsData,
  getTicksData,
} from '@/api/datafeed.api';

const STORAGE_KEY = 'navbar-selected-instrument';

export function useNavbarInstrument() {
  const { isLiveFeedActive, isMarketOpen } = useFeedStatus();
  const [instruments, setInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(() => {
    return getLocal(STORAGE_KEY) || 'NIFTY';
  });

  const [spotToken, setSpotToken] = useState(null);
  const [spotPrice, setSpotPrice] = useState(null);
  const [spotChange, setSpotChange] = useState(null);
  const [spotChangePercent, setSpotChangePercent] = useState(null);

  const [futToken, setFutToken] = useState(null);
  const [futPrice, setFutPrice] = useState(null);
  const [futChange, setFutChange] = useState(null);
  const [futChangePercent, setFutChangePercent] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const tokenCache = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getOptionChainInstrumentNames();
      if (cancelled || !res) return;
      const values = res?.data?.values ?? res?.values ?? [];
      const names = values.map((i) => i.tradingName);
      if (names.length) {
        setInstruments(names);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedInstrument) return;
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        if (tokenCache.current[selectedInstrument]) {
          const cached = tokenCache.current[selectedInstrument];
          if (!cancelled) {
            setSpotToken(cached.spotToken);
            setFutToken(cached.futToken);
            setSpotPrice(cached.spotPrice);
            setSpotChange(cached.spotChange);
            setSpotChangePercent(cached.spotChangePercent);
            setFutPrice(cached.futPrice);
            setFutChange(cached.futChange);
            setFutChangePercent(cached.futChangePercent);
          }
          return;
        }

        const exchange = (selectedInstrument === 'SENSEX' || selectedInstrument === 'BANKEX') ? 'BSE' : 'NSE';
        
        const spotRes = await getSpotToken(selectedInstrument, exchange);
        if (cancelled) return;
        const spotItems = spotRes?.data ?? spotRes ?? [];
        const sTkn = spotItems[0]?.instrumentToken ? String(spotItems[0].instrumentToken) : null;

        const expiryRes = await getOptionExpiries(selectedInstrument);
        if (cancelled) return;
        const expiries = expiryRes?.data?.expiries ?? expiryRes?.expiries ?? [];
        const firstExpiry = expiries[0];

        let fTkn = null;
        if (firstExpiry) {
          const chainRes = await getOptionChainInstrumentsData(selectedInstrument, firstExpiry);
          if (!cancelled && chainRes) {
            const chainPayload = chainRes?.data ?? chainRes ?? {};
            const futExp = chainPayload.fut?.['expiries']?.[0];
            const fTknRaw = chainPayload.fut?.[futExp]?.[0]?.instrumentToken;
            if (fTknRaw) fTkn = String(fTknRaw);
          }
        }

        if (cancelled) return;
        setSpotToken(sTkn);
        setFutToken(fTkn);

        const tokensToFetch = [sTkn, fTkn].filter(Boolean);
        if (tokensToFetch.length > 0) {
          const tickRes = await getTicksData(tokensToFetch.join(','));
          if (cancelled || !tickRes) return;
          const tickData = tickRes?.data ?? tickRes ?? {};

          let sPrice = null, sChg = null, sPct = null;
          let fPrice = null, fChg = null, fPct = null;

          if (sTkn && tickData[sTkn]) {
            const tk = tickData[sTkn];
            sPrice = tk.lp;
            if (tk.prev?.p) {
              sChg = tk.lp - tk.prev.p;
              sPct = (sChg / tk.prev.p) * 100;
            }
          }

          if (fTkn && tickData[fTkn]) {
            const tk = tickData[fTkn];
            fPrice = tk.lp;
            if (tk.prev?.p) {
              fChg = tk.lp - tk.prev.p;
              fPct = (fChg / tk.prev.p) * 100;
            }
          }

          setSpotPrice(sPrice);
          setSpotChange(sChg);
          setSpotChangePercent(sPct);

          setFutPrice(fPrice);
          setFutChange(fChg);
          setFutChangePercent(fPct);

          tokenCache.current[selectedInstrument] = {
            spotToken: sTkn,
            futToken: fTkn,
            spotPrice: sPrice,
            spotChange: sChg,
            spotChangePercent: sPct,
            futPrice: fPrice,
            futChange: fChg,
            futChangePercent: fPct,
          };
        }
      } catch (err) {
        console.error('[useNavbarInstrument] Fetch token details error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedInstrument]);

  const socketTokens = [spotToken, futToken].filter(Boolean);
  const handlePriceMessage = useCallback((token, tickData) => {
    if (!tickData || !tickData.lp) return;
    const lp = tickData.lp;

    if (token === spotToken) {
      setSpotPrice(lp);
      const cached = tokenCache.current[selectedInstrument];
      const prevP = cached ? cached.spotPrice - (cached.spotChange || 0) : null;
      if (prevP) {
        const chg = lp - prevP;
        const pct = (chg / prevP) * 100;
        setSpotChange(chg);
        setSpotChangePercent(pct);
      }
    } else if (token === futToken) {
      setFutPrice(lp);
      const cached = tokenCache.current[selectedInstrument];
      const prevP = cached ? cached.futPrice - (cached.futChange || 0) : null;
      if (prevP) {
        const chg = lp - prevP;
        const pct = (chg / prevP) * 100;
        setFutChange(chg);
        setFutChangePercent(pct);
      }
    }
  }, [spotToken, futToken, selectedInstrument]);

  useSocketFeed(
    isLiveFeedActive && isMarketOpen && socketTokens.length > 0,
    socketTokens,
    handlePriceMessage
  );

  const [priceType, setPriceType] = useState(() => {
    return getLocal('navbar-price-type') || 'Spot';
  });

  const handlePriceTypeChange = (type) => {
    setLocal('navbar-price-type', type);
    setPriceType(type);
  };

  const handleInstrumentChange = (value) => {
    setLocal(STORAGE_KEY, value);
    setSelectedInstrument(value);
  };

  return {
    instruments,
    selectedInstrument,
    spotPrice,
    spotChangePercent,
    futPrice,
    futChangePercent,
    priceType,
    isLoading,
    handleInstrumentChange,
    handlePriceTypeChange,
  };
}
